import React from 'react';
import { useSession } from "./SessionContext";
import { Button, Intent, Section, SectionCard, Icon } from "@blueprintjs/core";
import { IconNames } from "@blueprintjs/icons";
import { Sequences } from "./Experiments/Sequences/Sequences";
import { useNavigate } from "react-router";
import { sequenceGroupColors } from "../components/colors";
import { useLanguage } from "../context/LanguageContext";

export const LoggedInIndex = () => {
    const { userData } = useSession();
    const navigate = useNavigate();
    const { t } = useLanguage();

    const delay_until_second_week = 72000; // =20 Stunden
    const delay_until_third_week = 691200; // 8 Tage
    const current_time = Math.floor(Date.now() / 1000);

    let week = "undefined";
    if (userData) {
        if (current_time < (userData["firstLoginTimeStamp"] + delay_until_second_week)) {
            week = "week1"
        } else {
            if (current_time < (userData["firstLoginTimeStamp"] + delay_until_third_week)) {
                week = "week2"
            } else {
                week = "week3"
            }
        }
    }

    return (
        <>
            <h1>{t('pages', 'loggedIn', 'welcome')}</h1>

            {/* Introduction text with increased font size and line height */}
            <p style={{
                fontSize: '1.1rem',
                lineHeight: '1.6',
                marginBottom: '1.5rem'
            }}>
                {t('pages', 'loggedIn', 'introduction')}
            </p>

            {/* Separate paragraph for the experiments title with bold styling */}
            <p style={{
                fontSize: '1.1rem',
                fontWeight: 'bold',
                marginBottom: '1.5rem'
            }}>
                {t('pages', 'loggedIn', 'experimentsTitle')}
            </p>

            {userData?.availableSequences?.[week]?.length === 0 && (
                <p>{t('pages', 'loggedIn', 'noExperiments')}</p>
            )}
            {userData?.availableSequences?.[week]?.map((sequenceId: string) => {
                const sequenceFactory = Sequences[sequenceId];
                if (!sequenceFactory) return null;
                const sequence = sequenceFactory();

                const isFinished = userData.finishedExperiments?.[sequenceId];
                // Goldener Rahmen, wenn abgeschlossen, sonst Standard-Farbe über Gruppe
                const borderColor = isFinished
                    ? "#FFC940"
                    : (sequenceGroupColors[sequence.group ?? ""] || "#e0e0e0");

                return (
                    <Section
                        key={sequenceId}
                        title={
                            <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                {isFinished && <Icon icon={IconNames.ENDORSED} color="#FFC940" />}
                                {sequence.name}
                            </span>
                        }
                        style={{
                            marginBottom: "20px",
                            border: `3px solid ${borderColor}`,
                            borderRadius: 12,
                            boxShadow: "none"
                        }}
                        rightElement={
                            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                {isFinished && sequence.resultScreenIndex && (
                                    <Button
                                        small
                                        icon={IconNames.CHART}
                                        intent="success"
                                        onClick={() =>
                                            navigate(`/app/sequences/${sequenceId}/${sequence.resultScreenIndex}`)
                                        }
                                        style={{
                                            fontWeight: 500,
                                            borderRadius: 7
                                        }}
                                    >
                                        {t('pages', 'loggedIn', 'resultsButton')}
                                    </Button>
                                )}
                                {isFinished && sequence.explanationScreenIndex && (
                                    <Button
                                        small
                                        icon={IconNames.LIGHTBULB}
                                        intent={Intent.WARNING}
                                        style={{
                                            background: "#FFE29A",
                                            color: "#674200",
                                            fontWeight: 500,
                                            borderRadius: 7,
                                            border: "none",
                                            boxShadow: "0 1px 3px 0 rgba(0,0,0,0.05)"
                                        }}
                                        onClick={() =>
                                            navigate(`/app/sequences/${sequenceId}/${sequence.explanationScreenIndex}`)
                                        }
                                    >
                                        {t('pages', 'loggedIn', 'explanationButton')}
                                    </Button>
                                )}
                                <Button
                                    intent={Intent.PRIMARY}
                                    onClick={() => navigate(`/app/sequences/${sequenceId}/0`)}
                                    icon={IconNames.ARROW_RIGHT}
                                    text={isFinished ? t('pages', 'loggedIn', 'continueButton') : t('pages', 'loggedIn', 'startButton')}
                                    style={{
                                        fontWeight: 500,
                                        borderRadius: 7
                                    }}
                                />
                            </div>
                        }
                    >
                        <SectionCard style={{ boxShadow: "none" }}>
                            {sequence.description}
                        </SectionCard>
                    </Section>
                );
            })}
        </>
    );
}